import { createContext, useContext, type ReactNode } from 'react'

/**
 * Localizes any `<Sheet>` rendered within the provider so it portals into the
 * provided DOM container instead of `document.body`. Combined with
 * `position: absolute` (set in `sheet.tsx` when the container resolves to a
 * non-null node) the sheet anchors to the container's edges instead of the
 * browser viewport's. Combined with `overflow: hidden` on the container, the
 * default `slide-in-from-right` animation is clipped to the container so the
 * sheet visually emerges from the container's right edge — not the browser's.
 *
 * The context carries the container node and NOTHING else. It used to also
 * carry the backdrop's right inset in pixels, which changed on every pointer
 * move of the chat-sidebar resize handle and re-rendered every mounted sheet
 * — context updates bypass React.memo. That inset is now the CSS var
 * `--sheet-overlay-right`, published by AuthLayout's grid (see sheet.tsx).
 *
 * Provider attaches the container DOM node via a callback ref (`useState`
 * setter, not `useRef`) so consumers re-render once the node mounts.
 *
 * Current consumer: `CallLogsSheetWrapper` (Calls detail panel).
 */
const SheetContainerContext = createContext<HTMLElement | null>(null)

export function useSheetContainer(): HTMLElement | null {
  return useContext(SheetContainerContext)
}

export function SheetContainerProvider({
  container,
  children,
}: {
  container: HTMLElement | null
  children: ReactNode
}) {
  return (
    <SheetContainerContext.Provider value={container}>
      {children}
    </SheetContainerContext.Provider>
  )
}

/**
 * Identifies which tab a portaled overlay (`<Sheet>`, `<Popover>`, …) belongs
 * to. These portal into a container — or document.body — that lives OUTSIDE the
 * per-tab `<Activity>` trees, so an open overlay on a now-inactive tab keeps
 * painting over whatever tab is active — and because a hidden `<Activity>`
 * freezes its subtree, that leaked overlay can't even be closed from within.
 *
 * sheet.tsx / popover.tsx stamp the owning tab's ID onto the portaled DOM
 * (`data-tab-owner`) so the layout can hide overlays that don't belong to the
 * active tab — a purely DOM/CSS lever that works without re-rendering the
 * frozen subtree.
 */
const TabOwnerContext = createContext<string | null>(null)

export function useTabOwnerId(): string | null {
  return useContext(TabOwnerContext)
}

export function TabOwnerProvider({
  tabId,
  children,
}: {
  tabId: string
  children: ReactNode
}) {
  return (
    <TabOwnerContext.Provider value={tabId}>
      {children}
    </TabOwnerContext.Provider>
  )
}
